import { useCallback, useEffect, useMemo, useState } from "react";

import { Session } from "../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../general-ui/icon";
import { EUploadOptions, ImageUploadOptions, ImageUploader } from "../../../general-ui/image-uploader/image-uploader";

import { batch } from "react-redux";
import { useAppDispatch, useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { updateSession, updateWorkingSession } from "../../../../store/actions/admin/create-event/session";
import { updateWorkingEventSession } from "../../../../store/actions/admin/create-event";
import { useSessionDetailsV2 } from "../../../../hooks/channel.hooks";
import * as Signals from 'utils/event-emitter';
import TopRightButtonControlContainer from "@general-ui/button-control/button-control-wrapper";
import { useProjectDetailsEnabled } from "@admin/events/hooks/project-details";
import { useSessionUuidParamAdmin } from "@/utils/use-session-uuid-param";
import { OptionalComponent } from "@/utils/optional-component";

interface ISessionThumbnailProps {
	session: Session;
	isEditor?: boolean;
}

const SessionThumbnail = ({ session, isEditor }: ISessionThumbnailProps) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const liveEventSessions = useTypedSelector(state => state.LiveEventReducer.eventBundle?.sessions);
	const projectDetailsFlagIsOn = useProjectDetailsEnabled();
	const sessionParam = useSessionUuidParamAdmin();

	const [largeImage, setLargeImage] = useState<string | null>(session.pre_broadcast_thumbnail || null);
	const [openLargeImageModal, setOpenLargeImageModal] = useState<ImageUploadOptions>(null);

	// force the modal's image to update to the most recent pre_broadcast_thumbnail
	useEffect(() => {
		setLargeImage(session.pre_broadcast_thumbnail || null);
	}, [session.pre_broadcast_thumbnail]);

	const sessionDetailsV2FeatureFlagOn = useSessionDetailsV2();

	const dispatch = useAppDispatch();

	const isMultiSession = ((liveEventSessions || workingEvent?.sessions || []).length || 0) > 1;

	const getSessionImageSaveUrl = (uploadedUri: string) => {
		if (projectDetailsFlagIsOn && isMultiSession) {
			// if we are on a multi session project details project, we want to set the uploaded image to both the session image and the pre_broadcast_thumbnail
			return uploadedUri || session.image || null;
		}

		return session.image || null;
	};

	const saveLargeImage = (image: { original: string, edited: string; }) => {
		if (!session || !token) return;
		const workingEventSession: Session | undefined = workingEvent?.sessions.find(session => session.uuid === session.uuid);
		const updatedSession: Session = {
			...session,
			image: getSessionImageSaveUrl(image.edited),
			pre_broadcast_thumbnail: image.edited || session.pre_broadcast_thumbnail || null,
			// we are temporarily disabling post session thumbnails
			use_different_thumbnails: false,
		};
		setLargeImage(image.edited);
		// update database and redux
		batch(() => {
			dispatch(updateSession(updatedSession, token));
			dispatch(updateWorkingSession(updatedSession));
			workingEventSession && dispatch(updateWorkingEventSession(updatedSession));
		});
	};

	if (!session || !isEditor) return null;
	if (!sessionDetailsV2FeatureFlagOn) return null;
	return (
		<>
			<TopRightButtonControlContainer>
				<>
					{largeImage ? (
						<button onClick={() => setOpenLargeImageModal(EUploadOptions.edit)} className="round crop">
							<Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} />
						</button>
					) : null}
				</>

				<button className="round" onClick={() => setOpenLargeImageModal(EUploadOptions.upload)}>
					<Icon name={ICONS.IMAGE} size={14} color={COLORS.WHITE} />
				</button>

				<OptionalComponent display={sessionParam === session.uuid}>
					<button className="round" onClick={() => Signals.broadcastSignal('session-details-modal:open')}>
						<Icon name={ICONS.EDIT} size={14} color={COLORS.WHITE} />
					</button>
				</OptionalComponent>
			</TopRightButtonControlContainer>

			<ImageUploader
				originalImage={largeImage}
				onClose={() => setOpenLargeImageModal(null)}
				fileTitle="Image"
				onSave={saveLargeImage}
				open={openLargeImageModal}
			/>
		</>
	);
};

export default SessionThumbnail;
